import { cn } from "../utils";

/** The texture utilities live in `excellent/app/globals.css`. */
const VARIANT_CLASS = {
  grain: "section-grain",
  dots: "section-dots",
  lines: "surface-lines",
} as const;

export type TextureVariant = keyof typeof VARIANT_CLASS;

/**
 * TextureOverlay — a single, non-interactive texture layer.
 *
 * Absolutely-positioned `aria-hidden` div carrying one of the texture
 * utilities (defined in `excellent/app/globals.css`, each with a mode-aware
 * opacity dial):
 *
 *   • `grain` — film-grain fractal noise (blended soft-light/overlay). The
 *     historical default; pair over a `.section-gradient` wash so a flat
 *     tint stops reading as a plastic gradient.
 *   • `dots`  — accent-tinted ben-day halftone, pooled in the top-right
 *     corner. Adopts the surrounding `--section` hue. For section banners.
 *   • `lines` — neutral diagonal hatch pooled in the bottom-right corner.
 *     Opt-in card texture (empty-state / feature cards) — never the base Card.
 *
 * Drop it as the last child of a `relative overflow-hidden` container so the
 * texture clips to the rounded corners and sits behind content (give content
 * `relative z-10`).
 *
 *   <div className="relative overflow-hidden section-gradient">
 *     …content…
 *     <TextureOverlay variant="dots" />
 *   </div>
 */
export function TextureOverlay({
  variant = "grain",
  className,
}: {
  variant?: TextureVariant;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        VARIANT_CLASS[variant],
        "pointer-events-none absolute inset-0 z-0",
        className,
      )}
    />
  );
}

/**
 * GrainOverlay — back-compat alias for `<TextureOverlay variant="grain" />`.
 *
 * @deprecated Use `<TextureOverlay variant="grain" />` directly. Kept only for
 * existing call sites; remove once they migrate.
 */
export function GrainOverlay({ className }: { className?: string }) {
  return <TextureOverlay variant="grain" className={className} />;
}
